import {Component, OnInit, Renderer2, ViewChild} from '@angular/core';
import {AppContainerService} from '@mydocma/app-container';
import {MmViewService} from '../../../mm-view.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AgGridAngular} from 'ag-grid-angular';
import {BehaviorSubject, combineLatest, firstValueFrom, from, Observable, Subscription} from 'rxjs';
import {TreeNode, TreeOptions, TreeService} from '@mydocma/component-library/src/lib/tree';
import {filter, map, switchMap, tap} from 'rxjs/operators';
import {Layout} from '@docma-mm-core/layout/layout';
import {clearDictionary} from '@docma-mm-core/utils/utils';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {Pool} from '@docma-mm-core/pool/pool';
import {MmProjectDownloadService} from '../../../mm-project/mm-download.service';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {GetValue, SettingsService} from '../../../mydocma-settings/settings.service';
import {ProjectService} from '@mydocma/project';
import {MatLegacyButton} from '@angular/material/legacy-button';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {PoolEditViewService} from '../services/pool-edit-view.service';
import {MmFilterService} from '../../mm-filter.service';
import {PoolVisibilityEnum} from '../services/pool-form.service';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {MmFaultsLoaderService} from '../../../mm-faults/mm-faults-loader.service';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';
import { ConnectivityService } from '@mydocma/core';
import { PromptDialogService } from '@mydocma/component-library/src/lib/prompt-dialog';
import { Logger } from '@mydocma/common/src/lib/logger';

interface PoolScope {
  scope: number;
  text?: string;
  icon?: string;
  pools?: Pool[];
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-pool-left-pane',
  templateUrl: './pool-left-pane.component.html',
  styleUrls: ['./pool-left-pane.component.scss']
})
export class PoolLeftPaneComponent implements OnInit {

  public isDesktop: boolean;

  @ViewChild('tree') tree: AgGridAngular;
  isEllipsis = false;

  private reloadSubject = new BehaviorSubject<any>(null);

  public poolTreeOptions: TreeOptions;
  private treeService: TreeService;


  entityScopes = EntityScopeEnum;
  poolsLoaded: Pool[];
  poolScopesLoaded: PoolScope[] = [];

  private nodeExpandedStates: {[id: string]: {node: TreeNode, isExpanded: boolean}} = {};

  private isPoolFilterEventSuspended = false;

  private subscriptions: Subscription[] = [];

  private pendingPoolSelection: {PoolId?: number} = null;
  public selectedPoolIds = new Set<number>();
  public selectedPoolIdsSubject = new BehaviorSubject<Set<number>>(new Set());
  public selectedPoolIds$ = this.selectedPoolIdsSubject.asObservable();
  private isSelectionInProgress = false;
  private selectionTimerId: any = null;

  private user: User;
  private currentProject: MmProject;
  private filterState: any = null;

  private loadingProjectPoolsSubject = new BehaviorSubject<boolean>(false);

  scopeSortSetting: { [scope: string]: number };
  scopeSort: string;

  poolScopesSubject = new BehaviorSubject<PoolScope[]>([]);
  poolScopes$ = this.poolScopesSubject.asObservable();
  isPoolsLoaded = false;
  // poolFavoriteFilterModeSubject = new BehaviorSubject<LayoutFavoriteFilterModeEnum>(null);
  // poolFavoriteFilterMode$ = this.poolFavoriteFilterModeSubject.asObservable();

  permissionDefectPoolsVisible$ = this.poolEditViewService.permissionService.permissionDefectPoolsVisible$;
  permissionDefectPoolsCreate$ = this.poolEditViewService.permissionService.permissionDefectPoolsCreate$;
  permissionDefectPoolsUpdate$ = this.poolEditViewService.permissionService.permissionDefectPoolsUpdate$;
  permissionDefectPoolsDelete$ = this.poolEditViewService.permissionService.permissionDefectPoolsDelete$;

  constructor(private readonly mmViewService: MmViewService,
              public readonly appContainerService: AppContainerService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly downloadService: MmProjectDownloadService,
              private readonly notificationService: NotificationService,
              private readonly projectService: ProjectService,
              private readonly settingsService: SettingsService,
              public deviceService: DeviceService,
              private renderer2: Renderer2,
              private poolEditViewService: PoolEditViewService,
              private readonly mmFilterService: MmFilterService,
              private readonly faultsLoaderService: MmFaultsLoaderService,
              public readonly platformModeStateService: PlatformModeStateService,
              private readonly connectivityService: ConnectivityService,
              private readonly dialogService: PromptDialogService,
              private readonly logger: Logger) {

    this.loadingProjectPoolsSubject
      .pipe(
        untilDestroyed(this))
      .subscribe(loading => {
        this.isPoolsLoaded = !loading;
      });

    this.poolTreeOptions = {
      suppressChildrenCount: true,
      toolbar: {
        buttons: {
          expandCollapseAll: {hidden: true},
        }
      },
      getNodeId: (data: any) => {
        if (data.scope) {
          return 'scope-' + data.scope.toString();
        } else {
          return data.Id.toString();
        }
      },
      getNodeInfo: (node: TreeNode) => {
        if (node.data.scope) {
          const isExpanded = this.getNodeExpandedState(node);
          return {
            text: node.data.text,
            icon: node.data.icon,
            expanded: isExpanded,
            selectable: false
          };
        }
        return {
          text: `${node.data.Name} (${this.platformModeStateService.isLocalMode ? (node.data.LocalCount ?? 0) : (node.data.Count ?? 0)})`,
          selectable: false
        };
      },
      useFilterCallbackForEmptyFilter: true,
      isNodeInFilter: (node: TreeNode, filters: string[]) => {
        const filterStr = ((this.treeService.filterService.filters?.length ?? 0) > 0 ? this.treeService.filterService.filters[0].toLowerCase() : null);

        if (node.data.scope) {
          const hasVisiblePools = (node.data.pools.length > 0 && node.data.pools.some(l => !(filterStr != null && l.Name.toLowerCase().indexOf(filterStr) === -1) ));

          // always show user scope if there are no pools at all so user can create pools
          if (node.data.scope === EntityScopeEnum.User) {
            const hasAnyPools = this.poolsLoaded && this.poolsLoaded.length > 0;
            if (!hasAnyPools) {
              return true;
            }
          }

          if (!hasVisiblePools) {
            return false;
          }
        }
        else {
          if ( (filterStr != null && node.data.Name.toLowerCase().indexOf(filterStr) === -1) ) {
            return false;
          }
        }
        return null;
      },
      getNodeClass: (node: TreeNode) => {
        if (node.data.scope) {
          return 'pool-scope-node';
        }
        return 'pool-node';
      },
      getNodeChildren: (node: TreeNode) => {
        if (node.data.scope) {
          return node.data.pools;
        }
        return [];
      },
      onReady: async (ts: TreeService) => {
        this.treeService = ts;

        this.treeService.onDataLoaded.pipe(untilDestroyed(this)).subscribe(async nodes => {
          if (this.pendingPoolSelection && nodes?.length > 0) {
            this.selectPendingPool();
          } else if (this.selectedPoolIds.size > 0) {
            this.ensureSelectedPoolsVisible();
          }

          this.subscriptions.push(this.treeService.nodes$.pipe(untilDestroyed(this)).subscribe(async nodes => {
            const f = await firstValueFrom(this.treeService.filterService.filters$);
            if (f && (this.treeService.filterService.filters?.length ?? 0) === 1 && this.treeService.filterService.filters[0] == '') {
              await this.loadNodeExpandedState(nodes);
            }
          }));

          this.subscriptions.push(this.treeService.onNodeExpanded.pipe(untilDestroyed(this)).subscribe(async n => {
            const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')) );
            if (!hasFilter && n) {
              if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
                this.nodeExpandedStates[n.id].isExpanded = true;
              }
              else {
                this.nodeExpandedStates[n.id] = {node: n, isExpanded: true};
              }
            }
          }));

          this.subscriptions.push(this.treeService.onNodeCollapsed.pipe(untilDestroyed(this)).subscribe(async n => {
            const hasFilter = await firstValueFrom(this.treeService.filterService.filters$.pipe(map(f => (f?.length ?? 0) > 0 && f[0] != '')));
            if (!hasFilter && n) {
              if (this.nodeExpandedStates.hasOwnProperty(n.id)) {
                this.nodeExpandedStates[n.id].isExpanded = false;
              }
              else {
                this.nodeExpandedStates[n.id] = {node: n, isExpanded: false};
              }
            }
          }));

          if (this.selectedPoolIds.size > 0) {
            setTimeout(() => {
              this.ensureSelectedPoolsVisible();
            }, 100);
          }
        });

        this.treeService.filterService.filters$.pipe(untilDestroyed(this)).subscribe(async filter => {
          if (!!this.isPoolFilterEventSuspended) {
            return;
          }
        });
      }
    };


    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this), tap(userPermissions => {
        this.user = userPermissions.data.User;
      })).toPromise();

    combineLatest([this.mmViewService.currentProject$,
      this.appContainerService.activeContentButton.pipe(filter(b => b && b.id === 'filter-pool'), tap(() => clearDictionary(this.nodeExpandedStates))),
      this.reloadSubject])
      .pipe(map(([currentProject, navButton]) => {
          if (!currentProject) {
            this.mmViewService.closeFilter();
            return;
          }
          if (navButton) {
            this.currentProject = currentProject;
            this.loadingProjectPoolsSubject.next(true);
            this.poolScopesSubject.next([]);
            return this.currentProject;
          }}),
        untilDestroyed(this))
      .pipe(switchMap(project =>
        this.downloadService.downloadPools(project).pipe(
          switchMap(pools => from(this.loadScopeSortSetting()).pipe(
            map(sortSetting => ({pools, scopeSortSetting: sortSetting})
            ))
          ))
      ))
      .subscribe(param => {
          this.subscriptions.forEach(s => s.unsubscribe());
          this.subscriptions = [];
          this.scopeSortSetting = param.scopeSortSetting;
          this.poolsLoaded = param.pools;
          if (this.platformModeStateService.isLocalMode) {
            this.setLocalCountsForPools();
          }

          this.getPoolScopes();
          this.poolScopesSubject.next(this.poolScopesLoaded);
          this.loadingProjectPoolsSubject.next(false);

          this.initializeFromFilterState();

          if (this.pendingPoolSelection && this.treeService) {
            this.selectPendingPool();
          } else if (this.selectedPoolIds.size > 0) {
            this.ensureSelectedPoolsVisible();
          }
        },
        err => {
          this.logger.error(err, '', 'PoolLeftPaneComponent');
          this.notificationService.error('An error while loading project layouts');
          this.loadingProjectPoolsSubject.next(false);
        });

    this.poolEditViewService.newPoolCreated$.pipe(untilDestroyed(this)).subscribe(params => {
      if (params?.Pool) {
        this.addedNode(params);
        // this.addPoolSelection(params.Pool.Id);
        // this.forceSelectPool(params.Pool.Id);
      }
    });

    this.poolEditViewService.poolUpdated$.pipe(untilDestroyed(this)).subscribe(params => {
      if (params?.Pool) {
        this.updateNode(params);
        // this.forceSelectPool(params?.Pool.Id);
      }
    });

    this.poolEditViewService.poolDeleted$.pipe(untilDestroyed(this)).subscribe(params => {
      if (params?.Pool) {
        this.deleteNode(params);
        if (this.selectedPoolIds.has(params?.Pool.Id)) {
          this.removePoolSelection(params.Pool.Id);
        }
      }
    });

    this.poolEditViewService.poolCountChanged$.pipe(untilDestroyed(this)).subscribe(poolCountChanges => {
      if (poolCountChanges && this.poolsLoaded) {
        this.updatePoolCounts(poolCountChanges, this.currentProject);
      }
      if(this.platformModeStateService.isLocalMode){
        this.setLocalCountsForPools().then(() => {
          this.getPoolScopes();
          this.poolScopesSubject.next(this.poolScopesLoaded);
        });
      }
    });

    this.faultsLoaderService.poolCountChanged$.pipe(untilDestroyed(this)).subscribe(poolCountChanges => {
      if (poolCountChanges && this.poolsLoaded) {
        this.updatePoolCounts(poolCountChanges, this.currentProject);
      }
    });

    this.mmFilterService.state.pipe(untilDestroyed(this)).subscribe(filterState => {
      this.filterState = filterState;

      if (filterState?.poolInclusionFilters) {
        const filterPoolIds = new Set(filterState.poolInclusionFilters.map(pool => pool.Id));

        const currentSelection = new Set(this.selectedPoolIds);
        const selectionDiffers = filterPoolIds.size !== currentSelection.size ||
          [...filterPoolIds].some(id => !currentSelection.has(id)) ||
          [...currentSelection].some(id => !filterPoolIds.has(id));

        if (selectionDiffers) {
          this.selectedPoolIds = filterPoolIds;
          this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
          this.refreshTreeStyling();
        }
      } else if (this.selectedPoolIds.size > 0) {
        this.selectedPoolIds.clear();
        this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
        this.refreshTreeStyling();
      }

      this.refreshTreeStyling();
    });

  }

  ngOnInit() {
    this.isDesktop = this.deviceService.isDesktop();
  }

  onPoolClick(pool: Pool) {
    if (pool?.Id) {
      const isCurrentlyIncluded = this.isPoolSelected(pool.Id);
      const isCurrentlyExcluded = this.isPoolExcluded(pool.Id);

      if (isCurrentlyExcluded) {
        this.mmFilterService.removePoolExclusionFilter(pool.Id);
        this.mmFilterService.addPoolInclusionFilter(pool);
      } else if (isCurrentlyIncluded) {
        this.mmFilterService.removePoolInclusionFilter(pool.Id);
      } else {
        this.mmFilterService.addPoolInclusionFilter(pool);
      }
    }

    // this.mmFilterService.setLayout(pool);
    // this.layoutStateService.setLayout(pool);
  }

  togglePoolSelection(poolId: number): void {
    if (this.selectedPoolIds.has(poolId)) {
      this.selectedPoolIds.delete(poolId);
    } else {
      this.selectedPoolIds.add(poolId);
    }
    this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
    this.refreshTreeStyling();
  }

  isPoolSelected(poolId: number): boolean {
    return this.selectedPoolIds.has(poolId);
  }

  isPoolExcluded(poolId: number): boolean {
    return this.filterState?.poolExclusionFilters?.some(pool => pool.Id === poolId) || false;
  }

  addPoolSelection(poolId: number): void {
    this.selectedPoolIds.add(poolId);
    this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
    this.refreshTreeStyling();
  }

  removePoolSelection(poolId: number): void {
    this.selectedPoolIds.delete(poolId);
    this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
    this.refreshTreeStyling();
  }

  getSelectedPoolIds(): number[] {
    return Array.from(this.selectedPoolIds);
  }



  private async initializeFromFilterState(): Promise<void> {
    try {
      const filterState = await firstValueFrom(this.mmFilterService.state);
      if (filterState?.poolInclusionFilters?.length > 0) {
        const currentlyFilteredPoolIds = filterState.poolInclusionFilters.map(pool => pool.Id);

        for (const poolId of currentlyFilteredPoolIds) {
          if (this.poolsLoaded?.some(p => p.Id === poolId)) {
            this.selectedPoolIds.add(poolId);
          }
        }

        this.selectedPoolIdsSubject.next(new Set(this.selectedPoolIds));
        this.refreshTreeStyling();
      }
    } catch (error) {
      this.logger.error(error, '', 'PoolLeftPaneComponent');
    }
  }

  private ensureSelectedPoolsVisible(): void {
    if (!this.treeService || this.selectedPoolIds.size === 0) {
      return;
    }

    this.selectedPoolIds.forEach(poolId => {
      const poolToSelect = this.poolsLoaded?.find(l => l.Id === poolId);
      if (poolToSelect) {
        const scopeNode = this.treeService.getNodeById('scope-' + (poolToSelect.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User).toString());
        if (scopeNode && !scopeNode.expanded) {
          this.treeService.expandNode(scopeNode);
        }
      }
    });
  }

  private refreshTreeStyling(): void {
    if (this.treeService?.gridApi) {
      this.treeService.gridApi.refreshCells({force: true});
    }
  }

  public getTooltipText(node: any): string {
    return this.isEllipsis ? node.text : '';
  }

  public onMouseEnter($event: HTMLElement) {
  this.isEllipsis = $event.offsetWidth < $event.scrollWidth;
  const textElement = $event.querySelector('.node-text-part') as HTMLElement;
  if (textElement) {
      this.isEllipsis = textElement.offsetWidth < textElement.scrollWidth;
    } else {
      this.isEllipsis = $event.offsetWidth < $event.scrollWidth;
    }
  }



  public toggleMenu(value: MatLegacyButton, state: 'close' | 'open'): void {

    const elem = value._elementRef.nativeElement.closest('.mydocma-node');
    if (state === 'close') {
      this.renderer2.removeClass(elem, 'item-menu-open');
    } else {
      this.renderer2.addClass(elem, 'item-menu-open');
    }
  }

  onCloseClick() {
    this.mmViewService.closeFilter();
  }

  getNodeExpandedState(node: TreeNode) {
    if (this.nodeExpandedStates.hasOwnProperty(node.id)) {
      return this.nodeExpandedStates[node.id].isExpanded;
    }
    return true;
  }

  async loadNodeExpandedState(nodes: TreeNode[]) {
    Object.keys(this.nodeExpandedStates).forEach(key => {
      const n = nodes.find(f => f.id === key);
      if (n) {
        if (this.nodeExpandedStates[key].isExpanded) {
          this.treeService.expandNode(n);
        } else {
          this.treeService.collapseNode(n);
        }
      }
    });

    if (this.selectedPoolIds.size > 0) {
      setTimeout(() => {
        this.ensureSelectedPoolsVisible();
      }, 100);
    }
  }

  private forceSelectPool(poolId: number): void {
    if (!this.treeService || !poolId) {
      return;
    }

    if (this.selectionTimerId) {
      clearTimeout(this.selectionTimerId);
      this.selectionTimerId = null;
    }

    if (this.isSelectionInProgress) {
      this.selectionTimerId = setTimeout(() => {
        this.forceSelectPool(poolId);
      }, 150);
      return;
    }

    this.isSelectionInProgress = true;

    try {
      const poolToSelect = this.poolsLoaded?.find(l => l.Id === poolId);
      if (!poolToSelect) {
        this.isSelectionInProgress = false;
        return;
      }

      const scopeNode = this.treeService.getNodeById('scope-' + (poolToSelect.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User).toString());
      if (scopeNode && !scopeNode.expanded) {
        this.treeService.expandNode(scopeNode);
      }

    } finally {
      setTimeout(() => {
        this.isSelectionInProgress = false;
      }, 50);
    }
  }

  private selectPendingPool() {
    if (!this.pendingPoolSelection || !this.treeService || !this.poolsLoaded?.length) {
      return;
    }

    let poolToSelect: Layout = null;
    if (!poolToSelect && this.pendingPoolSelection.PoolId) {
      poolToSelect = this.poolsLoaded.find(l => l.Id === this.pendingPoolSelection.PoolId);
    }

    if (poolToSelect) {
      this.addPoolSelection(poolToSelect.Id);

      // this.mmFilterService.setLayout(poolToSelect);
      // this.layoutStateService.setLayout(poolToSelect);

      this.ensureSelectedPoolsVisible();
    }

    this.pendingPoolSelection = null;
  }

  async loadScopeSortSetting(): Promise<{ [scope: string]: number }> {
    const defaultSortSetting: { [scope: string]: number } = {};
    defaultSortSetting[EntityScopeEnum.Project.toString()] = 1;
    defaultSortSetting[EntityScopeEnum.User.toString()] = 2;

    return await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project => from(Promise.resolve(defaultSortSetting))
        .pipe(switchMap(defaultValue => from(this.settingsService.load(PersonalizationKeysEnum.PoolTypeSorting, SettingNamesEnum.PoolTypeSorting, project))
          .pipe(
            tap(value => {
              this.scopeSort = value;
            }),
            map(value => {
              return GetValue<{ [scope: string]: number }>(value, defaultValue);
            })
          )
        ))
      )));
  }

  getPoolScopes() {
    const poolScopes: PoolScope[] = [];
    this.poolScopesLoaded = [];

    const projectScope = this.createPoolScope(EntityScopeEnum.Project);
    const userScope = this.createPoolScope(EntityScopeEnum.User);

    poolScopes.push(projectScope);
    poolScopes.push(userScope);

    projectScope.pools.push(...this.poolsLoaded
      .filter(x => !!x.IsPublic)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    userScope.pools.push(...this.poolsLoaded
      .filter(x => !x.IsPublic)
      .sort((a, b) => (a.Name ?? '').localeCompare((b.Name ?? ''))));

    this.poolScopesLoaded = poolScopes.sort((a, b) => this.scopeSortSetting[a.scope] - this.scopeSortSetting[b.scope]);
    return this.poolScopesLoaded;
  }

  private createPoolScope(scope: EntityScopeEnum): PoolScope {
    const poolScope: PoolScope = {scope, pools: []};
    poolScope.pools = [];
    switch (scope) {
      case EntityScopeEnum.User:
        poolScope.text = 'MM.pool.leftPane.scopeUserText';
        poolScope.icon = 'person_outline';
        break;
      case EntityScopeEnum.Project:
        poolScope.text = 'MM.pool.leftPane.scopeProjectText';
        poolScope.icon = 'domain';
        break;
      default:
        break;
    }
    return poolScope;
  }

  async toTopPosition(node: TreeNode) {
    const pool = Object.assign(new Pool(), node.data);
    const isExpanded = node.expanded;

    const sortedOthers = Object.keys(this.scopeSortSetting)
      .filter(key => +key !== node.data.scope)
      .sort((a, b) => +this.scopeSortSetting[a] - +this.scopeSortSetting[b]);

    for (let i = 0; i < sortedOthers.length; i++) {
      this.scopeSortSetting[sortedOthers[i]] = (i + 2);
    }
    this.scopeSortSetting[node.data.scope] = 1;

    await firstValueFrom(this.projectService.currentProject.pipe(
      switchMap(project =>
        this.settingsService.save(JSON.stringify(this.scopeSortSetting), PersonalizationKeysEnum.PoolTypeSorting, SettingNamesEnum.PoolTypeSorting, project))
    ));
    this.reloadSubject.next(true);
  }

  addPool(node: any) {
    if(this.showOfflineMessage()) return;

    this.poolEditViewService.showEditPoolDialog(node.data.scope, this.currentProject, this.user);
  }

  editPool(node: TreeNode) {
    if(this.showOfflineMessage()) return;

    const creator = this.user;
    // to do: get pool creator from somewhere

    const poolToEdit = Object.assign(new Pool(), node.data);
    this.poolEditViewService.showEditPoolDialog(
      poolToEdit.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User,
      this.currentProject,
      creator,
      poolToEdit
    );
  }

  deletePool(node: TreeNode) {
    if(this.showOfflineMessage()) return;

    const poolToDelete = Object.assign(new Pool(), node.data);
    this.poolEditViewService.deletePool(poolToDelete, this.currentProject);
  }

  filterExclusively(node: TreeNode) {
    if (node.data && !node.data.scope) {
      const pool = Object.assign(new Pool(), node.data);

      if (this.isPoolSelected(pool.Id)) {
        this.mmFilterService.removePoolInclusionFilter(pool.Id);
      }

      this.mmFilterService.addPoolExclusionFilter(pool);
    }
  }

  filterInclude(node: TreeNode) {
    if (node.data && !node.data.scope) {
      const pool = Object.assign(new Pool(), node.data);

      if (this.isPoolExcluded(pool.Id)) {
        this.mmFilterService.removePoolExclusionFilter(pool.Id);
      }

      this.mmFilterService.addPoolInclusionFilter(pool);
    }
  }

  private async updatePoolCounts(poolCountChanges: {poolId: number, countChange?: number, newCount?: number}[], project: MmProject) {
    const updatedPools: Pool[] = [];
    poolCountChanges.forEach(change => {
      const pool = this.poolsLoaded.find(p => p.Id === change.poolId);
      if (pool) {
        if (change.newCount !== undefined && pool.Count !== change.newCount) {
          if(this.platformModeStateService.isLocalMode){
            pool.LocalCount = change.newCount;
          }
          else{
            pool.Count = change.newCount;
          }
          updatedPools.push(pool);
        } else if (change.countChange !== undefined) {
          pool.Count = (pool.Count || 0) + change.countChange;
          if (pool.Count < 0) {
            pool.Count = 0;
          }
          updatedPools.push(pool);
        }
      }
    });

    if ((updatedPools?.length ?? 0) > 0) {
      await this.projectLoaderService.loadPoolsByAddedOrUpdated(project, updatedPools, true).toPromise();
    }

    this.getPoolScopes();

    for (const updatedPool of updatedPools) {
      this.updateNode({Pool: updatedPool, Pools: this.poolsLoaded});
    }
  }

  canUpdatePool(pool: Pool): Observable<boolean> {
    return this.poolEditViewService.permissionService.canUpdatePool(pool, this.user);
  }

  canDeletePool(pool: Pool): Observable<boolean> {
    return this.poolEditViewService.permissionService.canDeletePool(pool, this.user);
  }

  async addedNode(param: { Pool: Pool, Pools: Pool[] }) {
    this.poolsLoaded = param.Pools;

    const poolScope = (param.Pool.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User);
    const poolScopes = this.getPoolScopes();
    const poolScopeNode = poolScopes.find(x => x.scope === poolScope);
    let nodeIndex = poolScopeNode.pools.findIndex(x => x.Id === param.Pool.Id);

    const newTreeNode = {
      data: param.Pool
    } as TreeNode;

    const userPoolVisibility = +toPoolVisibility(this.user.Company.ViewPoint);
    const poolVisibleFor = toPoolVisibleForValue(param.Pool.VisibleFor);

    // tslint:disable-next-line:no-bitwise
    if ((poolVisibleFor & userPoolVisibility) === userPoolVisibility) {
      this.addNodeToSource(param.Pool, nodeIndex);
      nodeIndex = (nodeIndex === -1 ? 0 : nodeIndex);

      const addedNode = this.treeService.addNode(poolScopeNode.pools[nodeIndex], 'scope-' + poolScope.toString(), nodeIndex);

      this.notifyFilter();
    }

    this.treeService.gridApi.refreshCells({force: true});
    this.loadingProjectPoolsSubject.next(false);
  }

  async updateNode(param: { Pool: Pool, Pools: Pool[] }) {
    // Update the loaded layouts
    this.poolsLoaded = param.Pools;
    const poolScope = (param.Pool.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User);

    // Get the node to update from the tree
    let nodeToUpdate: { Node: Pool, Scope: PoolScope } = this.getNode(param.Pool);

    if (!nodeToUpdate){
      const poolScopeForNewPool = this.poolScopesLoaded.find(x => x.scope === poolScope);
      nodeToUpdate = {Node: param.Pool, Scope: poolScopeForNewPool};
    }

    const userPoolVisibility = +toPoolVisibility(this.user.Company.ViewPoint);
    const poolVisibleFor = toPoolVisibleForValue(param.Pool.VisibleFor);

    // tslint:disable-next-line:no-bitwise
    if ((poolVisibleFor & userPoolVisibility) === userPoolVisibility) {
      const nodeToUpdateScope = (nodeToUpdate.Node.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User);

      // Check if the scope has changed
      if (nodeToUpdate && nodeToUpdateScope !== poolScope || nodeToUpdate.Node.Name !== param.Pool.Name) {
        // Update the node properties
        nodeToUpdate.Node.Name = param.Pool.Name;
        nodeToUpdate.Node.Comment = param.Pool.Comment;
        nodeToUpdate.Node.CreatedOn = param.Pool.CreatedOn;
        nodeToUpdate.Node.Type = param.Pool.Type;
        nodeToUpdate.Node.VisibleFor = param.Pool.VisibleFor;
        nodeToUpdate.Node.IsPublic = param.Pool.IsPublic;

        // Get layout scopes and find the scope node to move the updated node to
        const poolScopes = this.getPoolScopes();
        const targetScopeNode = poolScopes.find(x => x.scope === poolScope);
        const nodeIndex = targetScopeNode.pools
          .findIndex(x => x.Id === param.Pool.Id);

        // Remove the node from the current scope and add it to the new scope
        this.treeService.removeNode(nodeToUpdate.Node);
        this.treeService.addNode(nodeToUpdate.Node, 'scope-' + poolScope.toString(), nodeIndex);
      }
      else if (nodeToUpdate) {
        nodeToUpdate.Node.Name = param.Pool.Name;
        nodeToUpdate.Node.Comment = param.Pool.Comment;
        nodeToUpdate.Node.CreatedOn = param.Pool.CreatedOn;
        nodeToUpdate.Node.VisibleFor = param.Pool.VisibleFor;
        nodeToUpdate.Node.IsPublic = param.Pool.IsPublic;
        this.treeService.updateNode(nodeToUpdate.Node);
      }
    }
    else {
      this.treeService.removeNode(nodeToUpdate.Node);
    }
    this.notifyFilter();
  }

  async deleteNode(param: { Pool: Pool, Pools: Pool[] }) {
    const nodeIndex = this.poolsLoaded.findIndex(x => x.Id === param.Pool.Id);
    this.treeService.removeNode(this.poolsLoaded[nodeIndex]);
    this.removeNodeFromSource(param.Pool);

    this.poolsLoaded = param.Pools;
    this.loadingProjectPoolsSubject.next(false);
    this.notifyFilter();
  }

  getNode(pool: Pool): {Node: Pool, Scope: PoolScope} {
    let n: {Node: Pool, Scope: PoolScope} = null;

    this.poolScopesLoaded.forEach(sc => {
      const node = sc.pools.find(x => x.Id === pool.Id);
      if (node) {
        n = {Node: node, Scope: sc};
        return;
      }
    });
    return n;
  }

  addNodeToSource(pool: Pool, index: number): boolean {
    let isAdded = false;
    this.poolScopesLoaded.forEach(sc => {
      if (sc.scope === (pool.IsPublic ? EntityScopeEnum.Project : EntityScopeEnum.User)) {
        if (sc.pools.findIndex(x => x.Id === pool.Id) === -1) {
          console.warn(`POOL ADDED TO NODE: ${pool.Name} (0)`);
          sc.pools.splice(index, 0, pool);
          isAdded = true;
        }
      }
    });
    return isAdded;
  }

  removeNodeFromSource(pool: Pool) {
    const n: {Node: Pool, Scope: PoolScope} = null;

    this.poolScopesLoaded.forEach(sc => {
      const i = sc.pools.findIndex(x => x.Id === pool.Id);
      if (i !== -1) {
        sc.pools.splice(i, 1);
      }
    });
  }

  private notifyFilter() {
    if (!this.treeService?.filterService){
      return;
    }

    if ((this.treeService.filterService.filters?.length ?? 0) === 0) {
      this.treeService.filterService.filters = [];
    }
    else if ((this.treeService.filterService.filters?.length ?? 0) === 1) {
      this.treeService.filterService.filters = [...this.treeService.filterService.filters];
    }
  }

  private showOfflineMessage(): boolean {
    if(this.connectivityService.isOfflineVal){
      this.dialogService.showOfflineNotificationDialog();
      return true;
    }
    return false;
  }

  private setLocalCountsForPools(): Promise<void> {
    return this.faultsLoaderService.getLocalPoolCount(this.currentProject)
      .then(poolCounts => {
        poolCounts.forEach(change => {
          const pool = this.poolsLoaded.find(p => p.Id === change.poolId);
          if (pool) {
            pool.LocalCount = change.newCount ?? 0;
          }
        });
    });
  }
}


export function toPoolVisibility(viewPoint: ViewPoint) {
  switch (viewPoint) {
    case ViewPoint.Contractor:
      return PoolVisibilityEnum.Contractor;
    case ViewPoint.Customer:
      return PoolVisibilityEnum.Customer;
    case ViewPoint.Subcontractor:
      return PoolVisibilityEnum.SubContractor;
  }
}

export function toPoolVisibleForValue(value: number | {
  SC?: boolean;
  CO?: boolean;
  CT?: boolean; }) {

  if (typeof value === 'number') {
    return value as number;
  }
  else {
    let visibleFor = 0;

    if ((value as {SC?: boolean; CO?: boolean; CT?: boolean; }).SC === true) {
      visibleFor += +PoolVisibilityEnum.SubContractor;
    }
    if ((value as {SC?: boolean; CO?: boolean; CT?: boolean; }).CO === true) {
      visibleFor += +PoolVisibilityEnum.Contractor;
    }
    if ((value as {SC?: boolean; CO?: boolean; CT?: boolean; }).CT === true) {
      visibleFor += +PoolVisibilityEnum.Customer;
    }
    return visibleFor;
  }
}
